<template>
  <v-card class="max-limited-height">
    <v-toolbar dark color="secondary">
      <v-toolbar-title> Client-sitting Management </v-toolbar-title>
      <v-spacer></v-spacer>
      <v-btn icon dark @click="close()">
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </v-toolbar>
    <v-row class="mx-0">
      <v-col cols="12">
        <v-stepper v-model="stepper" non-linear>
          <v-stepper-header>
            <v-stepper-step step="1" editable :complete="stepper > 1">
              Set initial details
            </v-stepper-step>
            <v-divider />
            <v-stepper-step step="2" editable>
              Assign clients to secondary coaches
            </v-stepper-step>
          </v-stepper-header>
          <v-stepper-items>
            <v-stepper-content step="1">
              <v-card-text>
                <div class="text-center ma-12" v-if="loading">
                  <v-progress-circular :indeterminate="loading" :size="50" color="blue"></v-progress-circular>
                </div>
                <v-form v-else>
                  <v-menu
                    ref="startDateMenu"
                    v-model="startDateMenu"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    min-width="290px"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="form.startDate"
                        v-bind="attrs"
                        v-on="on"
                        label="Start Date"
                        readonly
                      ></v-text-field>
                    </template>
                    <v-date-picker v-model="form.startDate" no-title scrollable @input="startDateMenu = false">
                    </v-date-picker>
                  </v-menu>
                  <v-menu
                    ref="endDateMenu"
                    v-model="endDateMenu"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    min-width="290px"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="form.endDate"
                        v-bind="attrs"
                        v-on="on"
                        label="End Date"
                        readonly
                      ></v-text-field>
                    </template>
                    <v-date-picker v-model="form.endDate" no-title scrollable @input="endDateMenu = false">
                    </v-date-picker>
                  </v-menu>

                  <v-textarea v-model="form.outOfOffice" auto-grow rows="3" label="Out of Office Message" persistent-hint hint="This message will appear on the web app, including your Client / Coach chats (optional)"/>

                  <v-autocomplete
                    v-model="form.secondaryCoaches"
                    :items="coaches"
                    :reduce="(coach) => `${coach.name}`"
                    @change="updateCoachListing()"
                    label="Secondary coaches"
                    chips
                    multiple
                    clearable
                    deletable-chips
                  >
                  </v-autocomplete>
                </v-form>
              </v-card-text>
              <div class="d-flex mb-1">
                <v-btn color="primary" class="ml-auto mr-3" @click="stepper = 2">
                  Continue
                </v-btn>
                <v-btn @click="close()"> Cancel </v-btn>
              </div>
            </v-stepper-content>
            <v-stepper-content step="2">
              <v-row>
                <v-col cols="5">
                  <v-card>
                    <div class="d-flex">
                      <v-card-title>Clients to assign</v-card-title>
                      <div v-if="clients && clients.length > 0" class="ma-2 ml-auto">
                        <v-btn v-if="allSelected" color="secondary" icon elevation="2" @click="deselectAllClients">
                          <v-icon>mdi-collapse-all</v-icon>
                        </v-btn>
                        <v-btn v-else color="secondary" icon elevation="2" @click="selectAllClients">
                          <v-icon>mdi-expand-all</v-icon>
                        </v-btn>
                      </div>
                    </div>
                    <v-card-text v-if="clients && clients.length == 0">
                      All clients have been assigned to a secondary coach.
                    </v-card-text>
                    <v-list v-else dense>
                      <v-list-item-group color="primary" class="overflow client-list">
                        <v-list-item v-for="client in clients" :key="client.id">
                          <template v-slot:default>
                            <v-list-item-action>
                              <v-checkbox
                                v-model="selected"
                                color="primary"
                                :label="client.name"
                                :value="client"
                              ></v-checkbox>
                            </v-list-item-action>
                            <v-list-item-content> </v-list-item-content>
                          </template>
                        </v-list-item>
                      </v-list-item-group>
                    </v-list>
                  </v-card>
                </v-col>
                <v-divider vertical inset />
                <v-col class="py-0">
                  <v-row v-if="form.secondaryCoaches.length > 0">
                    <v-col cols="12" class="d-inline-flex">
                      <v-select
                        v-model="selectedCoach"
                        :items="form.secondaryCoaches"
                        :item-text="(coach) => `${coach.name} (${coach.list.length} clients)`"
                        :item-value="(coach) => coach"
                      >
                      </v-select>
                      <v-btn color="primary" class="ml-5" @click="moveClients()">
                        Move
                      </v-btn>
                    </v-col>
                  </v-row>
                  <v-row v-else>
                    <v-col>
                      <v-card class="pa-3">
                        <v-card-title>
                          No secondary coaches selected yet
                        </v-card-title>
                      </v-card>
                    </v-col>
                  </v-row>
                  <v-row class="client-list overflow mr-0">
                    <v-col v-for="coach in form.secondaryCoaches" :key="coach.id" cols="6">
                      <v-card>
                        <v-card-title> {{ coach.name }} </v-card-title>
                        <v-card-subtitle> {{ coach.list.length }} clients</v-card-subtitle>
                        <v-list dense>
                          <v-list-item-group color="primary">
                            <v-list-item v-for="client in coach.list" :key="client.id">
                              <v-list-item-content class="list-name">
                                {{ client.name }}
                              </v-list-item-content>
                              <v-list-item-icon class="align-center">
                                <v-btn icon @click="removeClient(coach, client)">
                                  <v-icon small>mdi-close</v-icon>
                                </v-btn>
                              </v-list-item-icon>
                            </v-list-item>
                          </v-list-item-group>
                        </v-list>
                      </v-card>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-spacer />
              <div class="d-flex mb-1">
                <v-btn
                  color="secondary"
                  class="ml-auto mr-3"
                  :disabled="!canSubmit()"
                  :loading="buttonLoading"
                  @click="submit()"
                  >Submit</v-btn
                >
                <v-btn @click="stepper = 1"> Cancel </v-btn>
              </div>
            </v-stepper-content>
          </v-stepper-items>
        </v-stepper>
      </v-col>
    </v-row>
  </v-card>
</template>

<script>
import {
  SIMPLE_COACHES_QUERY,
  SIMPLE_CLIENTS_QUERY,
  SECONDARY_USERS_QUERY,
  UPSERT_SECONDARY_COACH_MUTATION,
} from "@/constants/graphql/coach/clientManagement";
import { getUserName } from "@/lib/username";
import { toLocalISOString } from "@/lib/timezone";

export default {
  name: "ClientManagement",
  props: {
    coachId: {
      type: String,
      required: false,
    },
    existingBlock: {
      type: Object,
      required: false,
    },
  },
  data() {
    return {
      loading: true,
      buttonLoading: false,
      stepper: null,
      form: {
        startDate: null,
        endDate: null,
        secondaryCoaches: [],
        outOfOffice: null,
      },
      defaultDate: null,
      startDateMenu: false,
      endDateMenu: false,
      selected: [],
      selectedCoach: null,
      clients: [],
      coaches: [],
    };
  },
  created() {
    this.loading = true;
    this.defaultDate = this.$moment()
      .add(1, "d")
      .format("YYYY-MM-DD");

    if (!this.existingBlock) {
      this.form.startDate = this.defaultDate;
      this.form.endDate = this.defaultDate;
    } else {
      this.form.startDate = this.$moment(this.existingBlock.startDate).format("YYYY-MM-DD");
      this.form.endDate = this.$moment(this.existingBlock.endDate).format("YYYY-MM-DD");
    }
  },
  computed: {
    allSelected() {
      return this.selected.length == this.clients.length;
    },
  },
  methods: {
    updateCoachListing() {
      let removedCoaches = this.coaches.filter(
        (coach) => coach.value.list.length > 0 && !this.form.secondaryCoaches.includes(coach.value)
      );

      removedCoaches.forEach((coach) => {
        coach.value.list.forEach((client) => {
          this.clients.unshift(client);
        });

        coach.value.list = [];
      });
    },
    selectAllClients() {
      this.selected = this.clients;
    },
    deselectAllClients() {
      this.selected = [];
    },
    moveClients() {
      if (this.selected.length > 0) {
        this.clients = this.clients.filter((client) => !this.selected.includes(client));
        let coach = this.form.secondaryCoaches.find((coach) => coach == this.selectedCoach);
        this.selected.forEach((value) => {
          coach.list.push(value);
        });

        this.selected = [];
      }
    },
    removeClient(coach, client) {
      let formCoach = this.form.secondaryCoaches.find((formCoach) => formCoach == coach);
      formCoach.list = formCoach.list.filter((coachClient) => coachClient != client);
      this.clients.unshift(client);
    },
    canSubmit() {
      return this.clients?.length == 0 && this.form.secondaryCoaches?.length > 0;
    },
    submit() {
      this.buttonLoading = true;

      let secondaryCoachInput = [];
      this.form.secondaryCoaches.forEach((coach) => {
        secondaryCoachInput.push({
          coach: coach && coach.id,
          clients: coach.list.map((client) => client.id),
        });
      });

      this.$apollo
        .mutate({
          mutation: UPSERT_SECONDARY_COACH_MUTATION,
          variables: {
            secondaryCoachPeriodId: this.existingBlock?.id || null,
            coachId: this.coachId || null,
            startDate: toLocalISOString(new Date(this.form.startDate)).substr(0, 10),
            endDate: toLocalISOString(new Date(this.form.endDate)).substr(0, 10),
            inputs: secondaryCoachInput,
            outOfOffice: this.form.outOfOffice,
          },
        })
        .then((response) => {
          if (response.data?.upsertSecondaryCoach.success) {
            this.buttonLoading = false;

            this.form = {
              startDate: null,
              endDate: null,
              secondaryCoaches: [],
            };
            this.selected = null;
            this.close();
          }
        });
    },
    close() {
      this.$emit("close");
    },
  },
  apollo: {
    coaches: {
      query: SIMPLE_COACHES_QUERY,
      variables() {
        return {
          coachId: this.coachId || null,
        };
      },
      fetchPolicy: "cache-and-network",
      update(data) {
        data.simpleCoaches.forEach((coach) => {
          let name = getUserName(coach);

          coach.value = {
            id: coach.id,
            name: name,
            list: [],
          };
          coach.text = name;
        });
        data.simpleCoaches.sort((a, b) => (a.value.name < b.value.name ? -1 : 1));

        data.simpleCoaches.unshift({
          value: {
            id: null,
            name: "No coach",
            list: [],
          },
          text: "No coach",
        });

        return data.simpleCoaches;
      },
    },
    clients: {
      query: SIMPLE_CLIENTS_QUERY,
      variables() {
        return {
          coachId: this.coachId || null,
        };
      },
      fetchPolicy: "cache-and-network",
      update(data) {
        data.simpleClients.forEach((client) => {
          client.name = getUserName(client);
        });
        data.simpleClients.sort((a, b) => (a.name < b.name ? -1 : 1));

        if (!this.existingBlock) {
          this.loading = false;
        }

        return data.simpleClients;
      },
    },
    existingBlock: {
      query: SECONDARY_USERS_QUERY,
      variables() {
        return {
          periodId: this.existingBlock.id,
        };
      },
      fetchPolicy: "network-only",
      update(data) {
        data.secondaryCoachUsers.forEach((coachUser) => {
          let coach = coachUser.coach;
          if (coach == null) {
            coach = { id: null };
          }

          let hasCoachSelected = this.form.secondaryCoaches.some((formCoach) => formCoach.id == coach.id);

          if (!hasCoachSelected) {
            let formCoach = this.coaches.find((simpleCoach) => simpleCoach.id == coach.id);
            if (formCoach.id) this.form.secondaryCoaches.push(formCoach.value);
            else this.form.secondaryCoaches.unshift(formCoach.value);
          }

          let formCoach = this.form.secondaryCoaches.find((formCoach) => formCoach.id == coach.id);

          let formUser = this.clients.find((user) => user.id == coachUser.user.id);
          if (formUser) {
            this.clients = this.clients.filter((client) => client.id != coachUser.user.id);

            if (!formCoach.list.some((user) => user.id == formUser.id)) {
              formCoach.list.push(formUser);
            }
          }
        });

        this.loading = false;
      },
      skip() {
        return (
          !this.existingBlock ||
          this.clients.length == 0 ||
          this.form.secondaryCoaches > 0 ||
          this.coaches.length == 0 ||
          !this.loading
        );
      },
    },
  },
};
</script>

<style scoped>
.list-name {
  font-size: 16px;
}
.max-limited-height {
  max-height: 650px;
}
.client-list {
  height: 300px;
}
.overflow {
  overflow: auto;
}
</style>
